import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

import { BadRequestError, NotFoundError, UnauthorizedError } from '../../../../../error';
import { addTrackCrumb, trackError } from '../../../../../error/tracking';
import { captureException } from '../../../../../logger/capture-exception';
import { getQueryClient } from '../../../../../sdk/request';
import { getAuthUserFromCache, useOrganizationUsersList } from '../../../../../services/storage';
import { fetchBrandsListImperative } from '../../../../../services/storage/brands-queries';
import { prefetchOrganizationIntegrations } from '../../../../../services/storage/organization-integrations-queries';
import {
  orgUsersListQueryFn,
  organizationUsersKeys,
  useCurrentOrgUserRole,
} from '../../../../../services/storage/organization-users-queries';
import {
  ORGANIZATION_BY_ID_STALE_MS,
  organizationByIdQueryFn,
  organizationsKeys,
  useOrganizationById,
} from '../../../../../services/storage/organizations-queries';
import { HeaderSettings } from './-components/header';

export const Route = createFileRoute(
  '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout'
)({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout',
      level: 'info',
    });
  },
  loader: async function ({ params: { id } }) {
    const queryClient = getQueryClient();
    const results = await Promise.allSettled([
      fetchBrandsListImperative(queryClient),
      queryClient.ensureQueryData({
        queryKey: organizationsKeys.byId(id),
        queryFn: function () {
          return organizationByIdQueryFn(id);
        },
        staleTime: ORGANIZATION_BY_ID_STALE_MS,
      }),
      queryClient.fetchQuery({
        queryKey: organizationUsersKeys.byOrgId(id),
        queryFn: function () {
          return orgUsersListQueryFn(queryClient, id);
        },
        staleTime: 0,
      }),
      prefetchOrganizationIntegrations(id),
    ]);

    const orgRejected = results.some(function (single) {
      if (single.status !== 'rejected') {
        return false;
      }

      if (
        (single.reason instanceof BadRequestError &&
          single.reason.toString().indexOf('unable to fetch organization') !== -1) ||
        single.reason instanceof NotFoundError ||
        single.reason instanceof UnauthorizedError
      ) {
        return true;
      }

      return false;
    });

    if (orgRejected) {
      window.location.href = '/app';
      return;
    }

    return id;
  },
  // eslint-disable-next-line no-use-before-define
  component: OrganizationSettingsLayout,
});

function OrganizationSettingsLayout() {
  const { id } = Route.useParams() as unknown as { id: string };
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // TanStack Query for organization details
  const { data: organization } = useOrganizationById(id);

  // TanStack Query hooks
  const queryClient = getQueryClient();
  const currentUser = getAuthUserFromCache(queryClient);

  // NOTE: Extract the organization ID from the current pathname to handle organization switching,
  //      this is necessary because when switching org from another org route, tanstack router is not updating correctly the org id.
  const orgIdFromPath = pathname.match(/\/organization\/([^/]+)/)?.[1];
  const currentOrgId = orgIdFromPath ?? id;

  // TanStack Query for org users by org ID
  const { data: organizationUsersData } = useOrganizationUsersList(currentOrgId);
  const organizationUsers = useMemo(() => organizationUsersData ?? [], [organizationUsersData]);

  const { isOwner, currentUserOrganization } = useCurrentOrgUserRole(
    currentOrgId,
    currentUser?._id
  );

  useEffect(
    function () {
      if (organization == null && organizationUsersData != null) {
        window.location.href = '/app';
        return;
      }

      if (pathname.includes('/plan') && !isOwner && organizationUsersData != null) {
        navigate({
          to: `/app/settings/organization/${currentOrgId}/users`,
          params: { id: currentOrgId },
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'OrganizationSettingsLayout',
              method: 'useEffect',
              infoMessage: 'unable to navigate to users',
            },
            trackError
          );
        });
      }
    },
    [organization, organizationUsersData, pathname, isOwner, navigate, currentOrgId]
  );

  return (
    <div className="h-full flex-1 overflow-y-auto">
      <div className="mx-auto flex h-screen w-full max-w-content flex-col p-4 md:p-0 md:pt-12">
        <HeaderSettings
          isOwner={isOwner}
          organization={organization}
          currentUserOrganization={currentUserOrganization}
          organizationUsers={organizationUsers.filter((u) => u != null)}
        />
        <Outlet />
      </div>
    </div>
  );
}
